/**
 * Reads + persists the user's preferred locale.
 *
 * Resolution order on mount:
 *   1. URL ?lang= (handled by i18next-browser-languagedetector)
 *   2. Authenticated user's club_central_users.preferred_locale
 *   3. localStorage 'vg.locale' (handled by detector)
 *   4. navigator.language (handled by detector)
 *
 * When the user explicitly picks a locale, we update i18next, persist to
 * localStorage (via i18next), AND write to the user's profile so the
 * preference follows them across devices.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { getLocale, type LocaleDefinition } from "@/i18n/config";

export function useLocalePreference() {
  const { i18n } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);

  // On mount, if the user is signed in, prefer their stored profile locale
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || cancelled) return;

      const { data } = await supabase
        .from("club_central_users")
        .select("preferred_locale")
        .eq("id", session.user.id)
        .maybeSingle();

      const stored = (data as { preferred_locale?: string | null } | null)?.preferred_locale;
      if (cancelled || !stored) return;

      // Only override if the user hasn't explicitly chosen something different
      // via URL/localStorage in this session
      const explicitlySetThisSession = typeof window !== "undefined"
        && new URLSearchParams(window.location.search).has("lang");
      if (!explicitlySetThisSession && i18n.language !== stored) {
        await i18n.changeLanguage(stored);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = useCallback(async (code: string) => {
    setIsUpdating(true);
    try {
      await i18n.changeLanguage(code);
      // Persist to profile if signed in (best-effort, silent failure is OK)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await supabase
          .from("club_central_users")
          .update({ preferred_locale: code })
          .eq("id", session.user.id);
      }
    } finally {
      setIsUpdating(false);
    }
  }, [i18n]);

  const current: LocaleDefinition | undefined = getLocale(i18n.language) ?? getLocale("en");

  return { current, setLocale, isUpdating };
}
